import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Permission } from 'zl-shared';

import { hasPermission } from '@/components/Authorize';
import { PrimaryButton } from '@/components/Buttons';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import Loader from '@/components/Loader';
import useOnMount from '@/hooks/useOnMount';
import { getInfiniteVideosQueryOptions } from '@/queries/media';
import { Video } from '@/queries/media/types';
import { trackMP } from '@/shared/mp';

import HomeHighlightsList from './HomeHighlightsList';
import HomeVideoSection, { MiniPlayerContentMode } from './HomeVideoSection';
import { useHabitatForStream, useIsAllHabitatsOffline } from './hooks';
import HomeHighlightVideoInfoBar from './InfoBar/HomeHighlightVideoInfoBar';
import HomeLiveStreamInfoBar from './InfoBar/HomeLiveStreamInfoBar';

const QUERY_KEY_PARAMS = { highlight: '3', sortOrder: 'desc', pageSize: 12 };

const HomeContent = () => {
  const isMobile = useIsMobileLayout();
  const ref = useRef<HTMLDivElement>(null);
  const [miniPlayerContentMode, setMiniPlayerContentMode] = useState<MiniPlayerContentMode>('highlight');

  const isAllHabitatsOffline = useIsAllHabitatsOffline();
  const habitatForStream = useHabitatForStream();

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery(
    getInfiniteVideosQueryOptions(QUERY_KEY_PARAMS),
  );
  const [highlightIndex, setHighlightIndex] = useState<number | undefined>();
  const currentHighlight = highlightIndex != null ? data?.list[highlightIndex] : undefined;

  const fetchMoreIfNeeded = (index: number) => {
    if (!data) return;
    // fetch more highlights if we are near the end
    if (!isFetchingNextPage && hasNextPage && index > data.list.length - 3) {
      fetchNextPage();
    }
  };

  const onNextHighlight = () => {
    if (!data || highlightIndex == null) return;
    const nextIndex = (highlightIndex + 1) % data.list.length;
    fetchMoreIfNeeded(nextIndex);
    setHighlightIndex(nextIndex);
  };

  const onHighlightClick = (highlight: Video) => {
    if (!data) return;
    const index = data.list.indexOf(highlight);
    fetchMoreIfNeeded(index);
    setHighlightIndex(index);
    setMiniPlayerContentMode('live-stream');
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trackMP('home-highlight-content-clicked', { indexInList: data?.list.indexOf(highlight), highlight });
  };

  const onSwitchContentMode = () => {
    setMiniPlayerContentMode((prev) => (prev === 'highlight' ? 'live-stream' : 'highlight'));
    trackMP('home-switched-miniplayer-mode', { miniPlayerCurrentContentMode: miniPlayerContentMode });
  };

  const onLoadMore = () => {
    fetchNextPage();
    trackMP('home-fetch-more-clicked', {
      miniPlayerCurrentContentMode: miniPlayerContentMode,
      habitatId: habitatForStream?._id,
      habitatName: habitatForStream?.title,
      zooName: habitatForStream?.zoo.name,
    });
  };

  const onCloseMiniPlayer = () => {
    trackMP('home-mini-player-closed', { currentHighlight });
    setHighlightIndex(undefined);
  };

  // set highlight to first video if all habitats are offline
  useEffect(() => {
    if (data?.list?.length && isAllHabitatsOffline) {
      setHighlightIndex((prev) => prev ?? 0);
      setMiniPlayerContentMode('live-stream');
    }
  }, [data?.list?.length, isAllHabitatsOffline]);

  useOnMount(() => {
    trackMP('home-visit', {
      currentHabitatId: habitatForStream?._id,
      currentHabitatInfo: habitatForStream,
      areAllHabitatsOffline: isAllHabitatsOffline,
    });
  });

  return (
    <div ref={ref} className="mobile:bg-white">
      <div className="top-0 z-10 mobile:sticky">
        <HomeVideoSection
          highlight={currentHighlight}
          habitat={habitatForStream}
          miniPlayerContentMode={miniPlayerContentMode}
          onNextHighlight={onNextHighlight}
          onSwitchContentMode={onSwitchContentMode}
          onCloseMiniPlayer={onCloseMiniPlayer}
        />
        {miniPlayerContentMode === 'highlight'
          ? habitatForStream && <HomeLiveStreamInfoBar habitat={habitatForStream} />
          : currentHighlight && <HomeHighlightVideoInfoBar highlight={currentHighlight} onNextHighlight={onNextHighlight} />}
      </div>
      <div className="px-4 py-3">
        <div className="preflight mb-3 flex justify-between border-b pb-3">
          <h4 className="preflight preflight-h4 text-sm font-bold">This month&apos;s highlights</h4>
          {hasPermission(Permission.Habitat.UploadMedia) && (
            <a href="/highlights" className="preflight preflight-a flex items-center gap-1 text-xs font-medium text-grey-3">
              <span>See all</span>
              <FontAwesomeIcon icon={faChevronRight} />
            </a>
          )}
        </div>
        <div className={currentHighlight || miniPlayerContentMode === 'live-stream' ? 'mobile:pb-24' : ''}>
          <HomeHighlightsList
            highlights={data?.list}
            currentHighlightIndex={highlightIndex}
            onHighlightClick={onHighlightClick}
          />
          {isFetchingNextPage && <Loader color="var(--charcoal)" className="my-4" width="64px" height="64px" />}
          {hasNextPage && !isFetchingNextPage && (
            <div className="my-4 flex justify-center">
              <PrimaryButton label="Load More" size={isMobile ? 'medium' : 'large'} onClick={onLoadMore} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeContent;
